
import * as d3 from "d3";
import uid from '../helpers/uid.js';


export interface ChartTooltipOptions {
    name: string
}
export interface ChartOptions {
    tooltip?: ChartTooltipOptions,
    upperLimit: number,
    lowerLimit: number,

    yAxisMin?: number,
    yAxisMax?: number,
}
export interface ChartData {
    epoch: number
    value: number
}
export interface LinePoint {
    x: number,
    y: number
}

// let dotColor = '#7492F2'
let lineColor = '#7492F2'
// let dotMovingColor = '#f074f2'


interface Margin {
    top: number
    right: number
    bottom: number
    left: number
}

interface Limits {
    upperLimit: d3.Selection<SVGPathElement, unknown, null, undefined>
    lowerLimit: d3.Selection<SVGPathElement, unknown, null, undefined>
}

export class ChartV2 {

    options: ChartOptions
    el: HTMLElement
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>
    margin: Margin = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    }
    timeParse: (dateString: string) => (Date | null)
    timeFormat: (date: Date) => string

    x?: d3.ScaleLinear<number, number, never>;
    y?: d3.ScaleLinear<number, number, never>;
    _xAxis: d3.Axis<d3.NumberValue>;
    _yAxis: d3.Axis<d3.NumberValue>;
    xAxis: (g: any, x: any) => any;
    yAxis: (g: any, y: any) => any;
    gx: d3.Selection<SVGGElement, unknown, null, undefined>;
    gy: d3.Selection<SVGGElement, unknown, null, undefined>;

    limits: Limits
    circles?: d3.Selection<SVGCircleElement, ChartData, SVGSVGElement, unknown>;
    // line?: d3.Line<ChartData>;
    plotLine: d3.Selection<SVGPathElement, unknown, null, undefined>;
    clip: any;
    data: ChartData[]
    zoom: d3.ZoomBehavior<any, any>
    firstDraw: Boolean;
    tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined>;
    width: number;
    height: number;


    constructor(element: HTMLElement, options?: ChartOptions) {
        this.data = [];
        this.el = element;
        this.firstDraw = false;
        if (options) {
            this.options = options;
        } else {
            this.options = {
                lowerLimit: 0,
                upperLimit: 0,
            }
        }

        this.timeParse = d3.timeParse('%s')
        this.timeFormat = d3.timeFormat('%m/%d %H:%M')

        const { offsetWidth: width, offsetHeight: height } = this.el;
        this.width = width;
        this.height = height;
        this.margin = {
            top: 20,
            bottom: 20,
            left: 30,
            right: 20,
        };

        this.clip = uid("clip");
        this.svg = d3.select(this.el).append('svg')
            // .attr("viewBox", `0 0 ${width + (this.margin.left + this.margin.right)} ${height + (this.margin.top + this.margin.bottom)}`)
            .attr("viewBox", `0 0 ${width} ${height}`)

        this.svg.append("clipPath")
            .attr("id", this.clip.id)
            .append("rect")
            .attr("x", this.margin.left)
            .attr("y", this.margin.top)
            .attr("width", width + this.margin.left + this.margin.right)
            .attr("height", height - this.margin.top - this.margin.bottom);

        this.svg
            .append("g")
            .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

        this.gx = this.svg.append('g')
        this.gy = this.svg.append('g')

        this.x = d3.scaleLinear()
            .domain(d3.extent(this.data, d => this.timeParse(d.epoch.toString())) as [Date, Date])
            .range([this.margin.left, width - this.margin.right])

        this._xAxis = d3.axisBottom(this.x!).ticks(width / 80).tickSizeOuter(0).tickFormat((d) => this.timeFormat(d as Date));
        this.xAxis = (g, x) => g
            .attr("transform", `translate(0,${height - this.margin.bottom})`)
            .attr("class", "x-axis")
            .call(this._xAxis.scale(x))

        this.y = d3.scaleLinear()
            .domain([
                this.options?.yAxisMin ?? d3.min(<number[]>this.data.map(d => d.value))!,
                this.options?.yAxisMax ?? d3.max(<number[]>this.data.map(d => d.value))!,
            ]
            )
            .range([height - this.margin.bottom, this.margin.top])
            // .range([height - margin.bottom, margin.top])
            .nice();
        this._yAxis = d3.axisLeft(this.y!).ticks(null, "s");
        this.yAxis = (g, y) => g
            .attr("transform", `translate(${this.margin.left},0)`)
            .attr("class", "y-axis")
            .call(this._yAxis.scale(y))
        // .call(g => g.select(".domain").remove())

        this.limits = {
            upperLimit: this.svg.append('path')!!,
            lowerLimit: this.svg.append('path')!!
        }

        this.plotLine = this.svg
            .append("path")
            .attr("clip-path", this.clip)
            .attr('class', 'plotline')
            .attr("fill", "none")
            .attr("stroke", lineColor)
            .attr("stroke-width", 2)

        this.zoom = d3.zoom()
            .scaleExtent([1, 100])
            .extent([[this.margin.left, 0], [width - this.margin.right, height]])
            .translateExtent([[this.margin.left, -Infinity], [width - this.margin.right, Infinity]])
            .on("zoom", this.zoomedFn());

        this.tooltip = d3
            .select(this.el)
            .append("div")
            .style("opacity", 0)
            .attr("class", "tooltip")
            .style("width", "140px")
            .style("position", "absolute")
            .style("background-color", "black")
            .style("color", "white")
            .style("border", "solid")
            .style("border-width", "1px")
            .style("border-radius", "5px")
            .style("font-size", "12px")
            .style("padding", "4px 4px 4px 6px")
            .style("text-align", "left");

        this.svg.append('line').classed('hoverLine', true)
    }

    draw(data: ChartData[]) {
        this._draw(data);
        if (!this.firstDraw) {
            this.firstDraw = true
            console.log('>>> data', JSON.stringify(data));
            this.firstDrawed();
        }
    }
    // plot(el: HTMLElement, data?: ChartData[]) {
    private _draw(data: ChartData[]) {
        this.data = data;
        let margin = this.margin;
        const { offsetWidth: width, offsetHeight: height } = this.el;

        if (!this.x) {
            this.x = d3.scaleLinear()
                .domain(d3.extent(data, d => this.timeParse(d.epoch.toString())) as [Date, Date])
                .range([margin.left, width - margin.right])
        } else {
            this.x!.domain(d3.extent(data, d => this.timeParse(d.epoch.toString())) as [Date, Date])
        }

        if (!this.y) {
            this.y = d3.scaleLinear()
                .domain([
                    this.options?.yAxisMin ?? d3.min(<number[]>data.map(d => d.value))!,
                    this.options?.yAxisMax ?? d3.max(<number[]>data.map(d => d.value))!,
                ]
                )
                .range([height - margin.bottom, margin.top])
                // .range([height - margin.bottom, margin.top])
                .nice();
        } else {
            this.y
                .domain([
                    this.options?.yAxisMin ?? d3.min(<number[]>data.map(d => d.value))!,
                    this.options?.yAxisMax ?? d3.max(<number[]>data.map(d => d.value))!,
                ]);
        }

        this.gx.call(this.xAxis, this.x)
        this.gy.call(this.yAxis, this.y)

        let limitline = d3
            .line<ChartData>()
            .x((d) => {
                return this.x!(d3.timeParse("%s")(d.epoch.toString())!);
            })
            .y((d) => this.y!(d.value));

        this.drawLimit(this.limits.upperLimit, this.options.upperLimit, limitline, { color: 'red', thick: 2.0 }, d3.min(data, d => this.timeParse(d.epoch.toString())) as Date)
        this.drawLimit(this.limits.lowerLimit, this.options.lowerLimit, limitline, { color: 'orange', thick: 2.0 }, d3.min(data, d => this.timeParse(d.epoch.toString())) as Date)

        this.drawLine(data, this.x!);
        this.drawCircles(data, this.x!);
    }

    private _line(data: ChartData[], x: d3.ScaleLinear<any, any, any>): string {
        return d3.line<ChartData>()
            .x((d) => x!(this.timeParse(d.epoch.toString())!))
            .y((d) => this.y!(d.value))
            .curve(d3.curveMonotoneX)(data)!;
    }

    private drawLine(data: ChartData[], x: d3.ScaleLinear<any, any, any>) {
        // let svg = this.svg.transition()
        this.plotLine
            // .transition()
            .attr("d", this._line(data, x)!)
        // .duration(750);
    }

    private drawCircles(data: ChartData[], x: d3.ScaleLinear<any, any, any>) {
        if (!this.circles) {
            this.circles = this.svg.selectAll('circle').data(data).enter().append('circle');
        }

        this.circles
            .data(data)
            .join('circle')
            // .on("click", this._onCircleClick())
            // .transition()
            // .duration(100)
            // .on("start", (d: any, i: any, nodes: any) => {
            //     d3.select(nodes[i])
            //         .style("fill", dotMovingColor)
            //         .attr("r", 5);
            // })
            .style("fill", '#FFFFFF')
            .style("stroke", '#7492F2')
            .style("stroke-width", 2)
            .attr("clip-path", this.clip)
            .attr('r', 3)
            .attr("cx", (d: ChartData) => x(this.timeParse(d.epoch.toString())!))
            .attr("cy", (d: ChartData) => this.y!(d.value))
            // .on("end", function (d: any, i: any, nodes: any) {
            //     d3.select(nodes[i])
            //         // .transition()
            //         // .duration(500)
            //         .style("fill", dotColor)
            //         .attr("r", 5);
            // });

        this.svg.selectAll('circle')
            .style("pointer-events", "all")
            .on("mouseover", this._onCircleClick())
            .on("mouseout", this._onCloseTooltip())

        // enable line cursor
        // this.svg.on('mousemove', this.onChartMouseOver())
    }


    private drawLimit(
        path: d3.Selection<SVGPathElement, unknown, null, undefined>,
        limit: number,
        limitline: d3.Line<ChartData>,
        style: any,
        min: Date | undefined,
    ) {
        let maxDate: number = Math.ceil((new Date()).getTime() / 1000);
        let minDate: number = !min ? 0 : Math.ceil((min).getTime() / 1000);
        path
            .attr("fill", "none")
            .attr("clip-path", this.clip)
            .attr("stroke", style.color)
            .attr("stroke-width", style.thick)
            .style("stroke-dasharray", ("3, 3"))
            .attr("d", limitline(
                [
                    <ChartData>{
                        epoch: minDate,
                        value: limit,
                    },
                    <ChartData>{
                        epoch: maxDate,
                        value: limit,
                    },
                ]
            ) as any);
    }

    private zoomedFn() {
        return (event: any) => {
            const xz = event.transform.rescaleX(this.x);

            this.drawLine(this.data, xz)
            this.drawCircles(this.data, xz)

            // this.gx.call(this.xAxis, xz);
            this.gx.call(this._xAxis.scale(xz));
            // this.x = xz;

        }
    }

    firstDrawed() {
        setTimeout(() => {
            this.svg.call(this.zoom)
                .transition()
                .duration(750)
                .call(this.zoom.scaleTo, 20, [this.x!(new Date()), 0]);
        }, 500);
    }

    onChartMouseOver() {
        return (e: any) => {
            // line bisector
            this.svg.select('.hoverLine');

            let mouseX = d3.pointer(e)[0];
            console.log(`mouseX`, mouseX);
            let mouseDate = this.x!.invert(mouseX!);
            console.log(`mouseDate`, (mouseDate));

            const mouseDateSnap = new Date(Math.floor(mouseDate))! //d3.timeYear.floor(!);
            console.log(`mouseDateSnap`, mouseDateSnap);

            // const mouseDate = this.x!.invert(e.layerX);
            console.log(`this.x!(mouseDateSnap)`, this.x!(mouseDateSnap) );
            console.log(`this.x!(mouseDate) < this.margin.left`, this.x!(mouseDateSnap) < this.margin.left);
            console.log(`this.x!(mouseDate) > this.width - this.margin.right`, this.x!(mouseDateSnap) > this.width - this.margin.right);

            if (this.x!(mouseDateSnap) < this.margin.left ||
               this.x!(mouseDateSnap) > this.width - this.margin.right) {
              return;
            }

            // const mouseDateSnap = d3.timeYear.floor(mouseDate);
            //

            const bisectEpoch = d3.bisector((d : ChartData) => d.epoch).right;
            const xIndex = bisectEpoch(this.data, Math.floor(mouseDateSnap.getTime() / 1000) , 1);
            console.log(`xIndex`, xIndex, 'data.length', this.data.length);
            // console.log('data', this.data);
            const dataPoint = this.data[xIndex];
            const value = dataPoint.value;
            const datetime = this.timeFormat(this.timeParse(dataPoint.epoch.toString())!)
            console.log(`y`, value);

            this.svg.selectAll('.hoverLine')
              .attr('x1', this.x!(mouseDate))
              .attr('y1', this.margin.top)
              .attr('x2', this.x!(mouseDate))
              .attr('y2', this.height - this.margin.bottom)
              .attr('stroke', '#147F90')
              .attr('fill', '#A6E8F2')
            ;

            let svgWidth = this.svg.node()!.getBoundingClientRect().width
            let svgHeight = this.svg.node()!.getBoundingClientRect().height
            const tooltipHeight = 46;
            const tooltipWidth = 140;

            let pY = e.layerY > svgHeight / 2 ? e.layerY - tooltipHeight : e.layerY;
            let pX = e.layerX > svgWidth / 2
              ? e.layerX + this.margin.right - tooltipWidth
              : e.layerX + this.margin.right ;

            this.tooltip.style("display", 'block');
            this.tooltip.style("opacity", .7);
            this.tooltip
                .html(this.tooltipMessage(this.options?.tooltip?.name ?? '', value.toString(), datetime) ?? ("Value is: " + e))
                .style("left", `${pX}px`)
                .style("top", `${pY}px`);
        };
    }

    private _onCircleClick() {
        return (e: any, d: any) => {
            console.log('eve', e);

            const tooltipHeight = 46;
            const tooltipWidth = 140;
            const offsetY = 10;

            let svgWidth = this.svg.node()!.getBoundingClientRect().width
            let svgHeight = this.svg.node()!.getBoundingClientRect().height

            let pY = e.layerY > svgHeight / 2 ? e.layerY - tooltipHeight - offsetY : e.layerY + offsetY;
            let pX = e.layerX > svgWidth / 2
              ? e.layerX + this.margin.right - tooltipWidth
              : e.layerX + this.margin.right ;

            let x = this.timeFormat(this.timeParse(d.epoch.toString())!)
            this.tooltip.style("display", 'block');
            this.tooltip.style("opacity", .7);
            this.tooltip
                .html(this.tooltipMessage(this.options?.tooltip?.name ?? '', d.value.toString(), x) ?? ("Value is: " + e))
                .style("left", `${pX}px`)
                .style("top", `${pY}px`);
            this.tooltip.select('#close').on('click', this._onCloseTooltip())

        }
    }

    tooltipMessage(a: string, b: string, c: string) {
        return `
        <div>
            ${a}: ${b}<br>${c}
        </div>
        `;
    }

    private _onCloseTooltip() {
        return () => this.tooltip.transition().duration(200).style("opacity", 0).on('end', () => {
            this.tooltip.style('display', 'none')
        });
    }
}
